import { Box, Typography } from '@mui/material';
import type { TooltipItem } from 'chart.js';
import 'chart.js/auto';
import { Bar } from 'react-chartjs-2';

import type { LicensedPeriodDPH, MonthlyDPH } from '@/types';

interface MonthlyUsageBarChartProps {
  monthlyDPH: MonthlyDPH;
  licensedPeriodDPH: LicensedPeriodDPH;
}

/**
 * Bar chart showing monthly DPH usage history
 * Two bars per month: Used hours and Available hours
 * Color coding based on thresholds
 */
export const MonthlyUsageBarChart = ({
  monthlyDPH,
  licensedPeriodDPH,
}: MonthlyUsageBarChartProps) => {
  // Check if data exists
  if (!monthlyDPH || !monthlyDPH.months || monthlyDPH.months.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No historical data available
        </Typography>
      </Box>
    );
  }

  const { months } = monthlyDPH;

  // Extract data for chart
  const labels = months.map((month) => `${month.month} (${month.dataProcHours} Hours)`);
  const usedDataProcHoursList = months.map((month) => month.usedDataProcHours);
  const availableDataProcHoursList = months.map((month) => {
    // Handle very small values for visibility
    if (month.availableDataProcHours > 0 && month.availableDataProcHours < 1) {
      return 1;
    } else if (month.availableDataProcHours < 0 && month.availableDataProcHours > -1) {
      return -1;
    }
    return month.availableDataProcHours;
  });
  const thresholdValueHoursList = months.map((month) => month.thresholdValueHours);
  const thresholdPercentageList = months.map((month) => month.thresholdPercentage);

  // Color coding for used hours (green = under threshold, red = over)
  const backgroundColorsUsedDataProcHours = usedDataProcHoursList.map((hours, index) => {
    return hours > thresholdValueHoursList[index]
      ? 'rgba(255, 99, 132, 0.7)'
      : 'rgba(3, 193, 165, 0.7)';
  });

  // Color coding for available hours (gray = positive, red = negative)
  const backgroundColorsAvailableDataProcHours = availableDataProcHoursList.map((hours) => {
    return hours < 0 ? 'rgba(255, 0, 0, 1)' : 'rgba(204, 204, 204, 0.7)';
  });

  const data = {
    labels,
    datasets: [
      {
        label: 'Used Hours',
        data: usedDataProcHoursList,
        backgroundColor: backgroundColorsUsedDataProcHours,
        borderWidth: 0,
      },
      {
        label: 'Available Hours',
        data: availableDataProcHoursList,
        backgroundColor: backgroundColorsAvailableDataProcHours,
        borderWidth: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    animation: false as const,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 20,
        },
        title: {
          display: true,
          text: 'Hours',
          font: {
            size: 14,
          },
        },
      },
    },
    plugins: {
      legend: {
        labels: {
          generateLabels: () => {
            return [
              { text: 'Used Hours - Under Threshold', fillStyle: 'rgba(3, 193, 165, 0.7)' },
              { text: 'Used Hours - Over Threshold', fillStyle: 'rgba(255, 99, 132, 0.7)' },
              { text: 'Available Hours - Remaining', fillStyle: 'rgba(204, 204, 204, 0.7)' },
              { text: 'Available Hours - Over', fillStyle: 'rgba(255, 0, 0, 1)' },
            ].map((label) => ({
              ...label,
              datasetIndex: -1,
              hidden: false,
            }));
          },
        },
        onClick: () => {
          // Disable legend click
        },
      },
      tooltip: {
        callbacks: {
          afterLabel(tooltipItem: TooltipItem<'bar'>) {
            if (tooltipItem.datasetIndex === 0) {
              return `Threshold: ${thresholdPercentageList[tooltipItem.dataIndex]}%`;
            }
            return '';
          },
        },
      },
    },
  };

  return (
    <>
      <Bar data={data} options={options} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          ({licensedPeriodDPH?.startDate} - {licensedPeriodDPH?.endDate})
        </Typography>
      </Box>
    </>
  );
};
